import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { pagosApi, mediosPagoApi } from '../api/pagos'
import EstadoBadgePago from '../components/EstadoBadgePago'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { useStoredState } from '../hooks/useStoredState'
import useDebounce from '../hooks/useDebounce'
import Boton from '../components/Boton'
import { colors, font, text, space, radius } from '../design/tokens'
import Paginador from '../components/Paginador'
import ModalReciboPresea from '../components/ModalReciboPresea'

const ESTADO_OPCIONES = [
  { value: 'pendiente',  label: 'Pendiente',  bg: '#fef3c7', color: '#92400e' },
  { value: 'confirmado', label: 'Confirmado', bg: '#dcfce7', color: '#166534' },
  { value: 'acreditado', label: 'Acreditado', bg: '#bbf7d0', color: '#14532d' },
  { value: 'rechazado',  label: 'Rechazado',  bg: '#fee2e2', color: '#991b1b' },
  { value: 'devuelto',   label: 'Devuelto',   bg: '#fed7aa', color: '#9a3412' },
  { value: 'anulado',    label: 'Anulado',    bg: '#f1f5f9', color: '#64748b' },
]

const FILTROS_DEFAULT = { q: '', estados: [], medio_pago_id: '', fecha_desde: '', fecha_hasta: '' }

export default function PagosPage() {
  const navigate = useNavigate()
  const [pagos, setPagos] = useState([])
  const [medios, setMedios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useStoredState('lgr.filtros.pagos', FILTROS_DEFAULT)
  const [paginacion, setPaginacion] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [showReciboPresea, setShowReciboPresea] = useState(false)

  useEffect(() => {
    mediosPagoApi.listar().then(({ data }) => setMedios(data)).catch(() => {})
  }, [])

  // Debounce del buscador (350 ms) + keep-previous-data (auditoría 1 jul 2026).
  const qBuscar = useDebounce(filtros.q, 350)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page,
        ...(qBuscar ? { q: qBuscar } : {}),
        ...(filtros.medio_pago_id ? { medio_pago_id: filtros.medio_pago_id } : {}),
        ...(filtros.fecha_desde ? { fecha_desde: filtros.fecha_desde } : {}),
        ...(filtros.fecha_hasta ? { fecha_hasta: filtros.fecha_hasta } : {}),
        ...(filtros.estados.length ? { estado: filtros.estados.join(',') } : {}),
      }
      const { data } = await pagosApi.listar(params)
      setPagos(data.data)
      setPaginacion({ current_page: data.current_page, last_page: data.last_page, total: data.total })
      setError(null)
    } catch {
      setError('No se pudieron cargar las cobranzas. Reintentá o limpiá los filtros.')
    } finally { setLoading(false) }
    // filtros.q NO es dependencia: el fetch usa qBuscar (debounced)
  }, [qBuscar, filtros.medio_pago_id, filtros.fecha_desde, filtros.fecha_hasta, filtros.estados, page])

  useEffect(() => { cargar() }, [cargar])

  const setFiltro = (k) => (e) => { setFiltros(f => ({ ...f, [k]: e.target.value })); setPage(1) }
  const toggleEstado = (v) => {
    setFiltros(f => ({ ...f, estados: f.estados.includes(v) ? f.estados.filter(x => x !== v) : [...f.estados, v] }))
    setPage(1)
  }

  const limpiar = () => { setFiltros(FILTROS_DEFAULT); setPage(1) }

  const hayFiltros = Boolean(
    filtros.q || filtros.medio_pago_id || filtros.fecha_desde || filtros.fecha_hasta || filtros.estados.length > 0
  )

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>Cobranzas</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {/* Recibo hecho EN PRESEA (retenciones), libre: se elige el cliente
              en el modal y el cobro entra acreditado a cuenta (Martin, 7 sep 2026). */}
          <Boton variante="secondary"
            title="El recibo se hizo en Presea (p. ej. con retenciones): cargá cliente y número y LGR lo trae como cobro acreditado a cuenta"
            onClick={() => setShowReciboPresea(true)}>
            🧾 Recibo hecho en Presea…
          </Boton>
          <Boton variante="primary" onClick={() => navigate('/pagos/nuevo')}>+ Nuevo pago</Boton>
        </div>
      </div>

      {showReciboPresea && (
        <ModalReciboPresea
          onClose={() => setShowReciboPresea(false)}
          onImportado={(pago) => navigate(`/pagos/${pago.id}`)}
        />
      )}

      <div style={st.filtros}>
        <input style={st.fi} placeholder="Buscar cliente, #ID o nº recibo..." value={filtros.q} onChange={setFiltro('q')} />
        <select style={st.fi} value={filtros.medio_pago_id} onChange={setFiltro('medio_pago_id')}>
          <option value="">Todos los medios</option>
          {medios.map(m => <option key={m.id} value={m.id}>{m.descripcion}</option>)}
        </select>
        <input style={st.fi} type="date" value={filtros.fecha_desde} onChange={setFiltro('fecha_desde')} />
        <input style={st.fi} type="date" value={filtros.fecha_hasta} onChange={setFiltro('fecha_hasta')} />
        <Boton variante={hayFiltros ? 'secondary' : 'ghost'} onClick={limpiar}>Limpiar</Boton>
      </div>
      <EstadoChipsFilter options={ESTADO_OPCIONES} selected={filtros.estados} onToggle={toggleEstado} />

      {!loading && !error && (
        <p style={st.info}>
          {paginacion.total} registro{paginacion.total !== 1 ? 's' : ''}
          {hayFiltros && <strong style={st.filtrosActivos}> · filtros activos</strong>}
        </p>
      )}

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              {['#ID', 'Fecha', 'Código', 'Cliente', 'Importe', 'Nº Recibo', 'Medio de pago', 'Estado', 'Acción'].map(h => (
                <th key={h} style={st.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && pagos.length === 0 ? (
              <tr><td colSpan={9} style={st.empty}>Cargando...</td></tr>
            ) : error ? (
              <tr><td colSpan={9} style={st.empty}>{error}</td></tr>
            ) : pagos.length === 0 ? (
              <tr><td colSpan={9} style={st.empty}>
                {hayFiltros ? (
                  <>
                    <div>No hay cobranzas que coincidan con los filtros.</div>
                    <Boton variante="secondary" sm style={{ marginTop: space.md }} onClick={limpiar}>Limpiar filtros</Boton>
                  </>
                ) : 'Sin resultados.'}
              </td></tr>
            ) : pagos.map(p => (
              <tr
                key={p.id}
                style={st.trCobranza}
                onClick={() => navigate(`/pagos/${p.id}`)}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
                title="Ver detalle del recibo"
              >
                <td style={st.tdId}>#{p.id}</td>
                <td style={st.tdFecha}>{fmtFecha(p.fecha)}</td>
                <td style={st.tdMono}>{p.cliente?.codigo_presea || '—'}</td>
                <td style={st.td}>
                  <div style={st.principal}>{p.cliente?.nombre}</div>
                  {p.cliente?.cuit && <div style={st.subtituloMono}>CUIT {p.cliente.cuit}</div>}
                </td>
                <td style={st.tdImporte}>${fmtMonto(p.importe)}</td>
                <td style={st.tdMono}>{p.numero_presea || '—'}</td>
                <td style={st.tdSec}>{p.medio_pago?.descripcion}</td>
                <td style={st.td}><EstadoBadgePago estado={p.estado} fallido={!!(p.cobro_fallido || p.cobro_fallido_at)} aDevolver={!!p.a_devolver_at && p.estado !== 'devuelto'} /></td>
                {/* Una sola acción contextual por estado; el resto (Editar /
                    Eliminar / Rechazar / Devolución) vive en el detalle,
                    que se abre clickeando la fila. */}
                <td style={st.tdAccion} onClick={e => e.stopPropagation()}>
                  {p.estado === 'pendiente' && (
                    <Boton
                      variante="success"
                      sm
                      title="Marcar como confirmado (la plata ya entró en banco/MP/Mobbex). Solo desde 'confirmado' se envía a Presea."
                      onClick={async () => {
                        if (!window.confirm('¿Confirmar este cobro? Marcalo solo si verificaste que la plata entró en banco / MP / Mobbex.')) return
                        try { await pagosApi.confirmar(p.id); cargar() } catch (e) { alert(e.response?.data?.message || 'Error al confirmar.') }
                      }}
                    >✓ Confirmar</Boton>
                  )}
                  {p.estado === 'confirmado' && p.envio_fallido && (
                    <span style={st.chipFallo} title={`El envío a Presea falló: ${p.envio_error || 'ver monitor'}. Reenvialo con el botón o desde /integraciones/presea.`}>⚠ Falló</span>
                  )}
                  {p.estado === 'confirmado' && (
                    p.envio_pendiente ? (
                      <span style={st.chipEnviado} title="Ya está encolado: Presea lo va a procesar en la próxima corrida (queda 'acreditado' cuando responda).">⏳ Enviado</span>
                    ) : (
                      <Boton
                        variante="send"
                        sm
                        title="Encolar este cobro para enviarlo a Presea (usar cuando la NV vinculada ya está procesada y el envío automático no lo agarró)."
                        onClick={async () => {
                          if (!window.confirm('¿Enviar este cobro a Presea ahora?')) return
                          try { await pagosApi.enviarPresea(p.id); cargar() } catch (e) { alert(e.response?.data?.message || 'Error al encolar.') }
                        }}
                      >→ Enviar a Presea</Boton>
                    )
                  )}
                  {!['pendiente', 'confirmado'].includes(p.estado) && <span style={st.sinAccion}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginador pagina={page} totalPaginas={paginacion.last_page} onCambiar={setPage} />
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  filtros: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  fi: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', minWidth: 140, background: colors.bg },
  info: { margin: `0 0 ${space.md}px`, color: colors.textSec, fontSize: text.sec },
  filtrosActivos: { color: colors.link, fontWeight: 600 },

  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 16px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  // Cobranzas tienen 2 líneas (cliente + CUIT) → 56px para que respire
  trCobranza: { borderBottom: `1px solid ${colors.borderSoft}`, height: 48, cursor: 'pointer' },
  td: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.25 },
  tdMono: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdId:   { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdFecha: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdSec: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, lineHeight: 1.25 },
  tdImporte: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right', lineHeight: 1.25 },
  tdAccion: { padding: '8px 16px', verticalAlign: 'middle' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  subtituloMono: { fontSize: text.meta, color: colors.textMeta, fontFamily: font.mono, marginTop: 2 },
  empty: { padding: 32, textAlign: 'center', color: colors.textMeta },

  chipEnviado: { padding: '4px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: radius.sm, fontSize: text.sec, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', cursor: 'default' },
  chipFallo: { padding: '4px 12px', background: colors.errBg, border: `1px solid ${colors.err}33`, borderRadius: radius.sm, fontSize: text.sec, fontWeight: 600, color: colors.err, whiteSpace: 'nowrap', cursor: 'default', marginRight: 6 },
  sinAccion: { color: '#cbd5e1' },

  paginacion: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: space.xl, marginTop: space.xl },
  pageBtn: { padding: '6px 14px', background: colors.bg, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body, color: colors.text },
  pageInfo: { color: colors.textSec, fontSize: text.body },
}
